import { useEffect, useState } from "react";
import { Sheet } from "../../components/Sheet";
import { Field, NumberInput, Segmented } from "../../components/fields";
import { Picker, MultiPicker, type PickerOption } from "../../components/Picker";
import { Button } from "../../components/ui";
import { useFacets } from "../../api/queries";
import { activeFilterCount, emptyFilters, type Filters } from "./filterState";
import { fieldLabels } from "../../i18n/fieldLabels";
import {
  kindLabels,
  metalLabels,
  optionsOf,
  ownershipLabels,
  stoneTypeLabels,
} from "../../i18n/choices";
import { pick } from "../../i18n/labels";
import { useT } from "../../i18n/strings";
import type { FacetCount, Metal, OwnershipStatus, PieceKind, StoneType } from "../../api/types";
import s from "./filters.module.css";

/** Options with the owner's own counts beside them, where the facets know one. */
function counted<T extends string>(options: PickerOption[], facets: FacetCount<T>[] | undefined) {
  if (!facets) return options;
  const counts = new Map(facets.map((row) => [row.value as string, row.count]));
  return options.map((option) => ({ ...option, count: counts.get(option.id) ?? 0 }));
}

interface FilterSheetProps {
  open: boolean;
  onClose: () => void;
  filters: Filters;
  onApply: (filters: Filters) => void;
}

/** Edits a draft and commits on Apply, so a half-typed purity does not refetch. */
export function FilterSheet({ open, onClose, filters, onApply }: FilterSheetProps) {
  const t = useT();
  const [draft, setDraft] = useState<Filters>(filters);
  const facets = useFacets();

  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const set = (changes: Partial<Filters>) => setDraft((current) => ({ ...current, ...changes }));
  const count = activeFilterCount(draft);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t.filters.title}
      headerAction={
        count > 0 ? (
          <button
            type="button"
            className={s.clear}
            onClick={() => setDraft({ ...emptyFilters, q: draft.q })}
          >
            {t.actions.clearAll}
          </button>
        ) : null
      }
      footer={
        <Button
          variant="primary"
          onClick={() => {
            onApply(draft);
            onClose();
          }}
        >
          {count > 0 ? `${t.actions.apply} · ${count}` : t.actions.apply}
        </Button>
      }
    >
      <div className={s.body}>
        <Field label={fieldLabels.kind} optional hint={t.filters.anyOf}>
          <MultiPicker
            title={pick(fieldLabels.kind)}
            options={counted(optionsOf(kindLabels), facets.data?.kind)}
            values={draft.kind}
            placeholder={t.filters.any}
            onChange={(ids) => set({ kind: ids as PieceKind[] })}
          />
        </Field>

        <Field label={fieldLabels.metal} optional hint={t.filters.anyOf}>
          <MultiPicker
            title={pick(fieldLabels.metal)}
            options={counted(optionsOf(metalLabels), facets.data?.metal)}
            values={draft.metal}
            placeholder={t.filters.any}
            onChange={(ids) => set({ metal: ids as Metal[] })}
          />
        </Field>

        <Field label={fieldLabels.purity_permille} optional>
          <NumberInput
            value={draft.purity}
            step="1"
            min={1}
            placeholder={t.filters.purityPlaceholder}
            onChange={(value) => set({ purity: value })}
          />
        </Field>

        <Field label={fieldLabels.ownership_status} optional hint={t.filters.anyOf}>
          <MultiPicker
            title={pick(fieldLabels.ownership_status)}
            options={counted(optionsOf(ownershipLabels), facets.data?.ownership_status)}
            values={draft.ownership_status}
            placeholder={t.filters.any}
            onChange={(ids) => set({ ownership_status: ids as OwnershipStatus[] })}
          />
        </Field>

        <Field label={fieldLabels.stones} optional>
          <Segmented
            name="has-stones"
            value={draft.has_stones === null ? "any" : draft.has_stones ? "yes" : "no"}
            options={[
              { value: "any", label: t.filters.any },
              { value: "yes", label: t.filters.withStones },
              { value: "no", label: t.filters.withoutStones },
            ]}
            onChange={(value) =>
              set({
                has_stones: value === "any" ? null : value === "yes",
                // "No stones" and a stone type cannot both hold.
                ...(value === "no" && { stone: null }),
              })
            }
          />
        </Field>

        <Field label={fieldLabels.stone_type} optional>
          <Picker
            title={pick(fieldLabels.stone_type)}
            options={optionsOf(stoneTypeLabels)}
            value={draft.stone}
            disabled={draft.has_stones === false}
            placeholder={t.filters.any}
            onChange={(id) => set({ stone: id as StoneType | null })}
          />
        </Field>
      </div>
    </Sheet>
  );
}
